import { addPhrase, removePhrase } from '@chat-rea/console-ui';

const MAX_MENU = 8;

const REFUSED = new Set(['as', 'acct', 'account', 'pm', 'help']);

export type PhraseMenuItem = {
  key: string;
  label: string;
  text: string;
  pick: 'insert' | 'unsave';
};

export type PhraseEnter =
  | { kind: 'send' }
  | { kind: 'insert'; text: string }
  | { kind: 'save'; phrases: readonly string[]; notice: string }
  | { kind: 'unsave'; phrases: readonly string[]; notice: string }
  | { kind: 'hold'; notice: string | null };

export type PhraseView = {
  menu: readonly PhraseMenuItem[];
  hint: string | null;
  enter: PhraseEnter;
};

const SEND: PhraseView = { menu: [], hint: null, enter: { kind: 'send' } };

function hold(notice: string | null, menu: readonly PhraseMenuItem[] = []): PhraseView {
  return { menu, hint: notice, enter: { kind: 'hold', notice } };
}

function slashParts(draft: string): { name: string; args: string; query: string } | null {
  const text = draft.trim();
  if (text === '' || !text.startsWith('/')) return null;
  const body = text.slice(1);
  const head = body.split(/\s+/, 1)[0] ?? '';
  return { name: head.toLowerCase(), args: body.slice(head.length).trim(), query: body.trim() };
}

function oneBased(ref: string, length: number): number | null {
  if (!/^\d+$/.test(ref)) return null;
  const index = Number(ref) - 1;
  return index >= 0 && index < length ? index : null;
}

function findPhrases(query: string, phrases: readonly string[]): readonly string[] {
  if (query === '') return phrases;
  const index = oneBased(query, phrases.length);
  const byIndex = index === null ? undefined : phrases[index];
  if (byIndex !== undefined) return [byIndex];
  const lowered = query.toLowerCase();
  return phrases.filter((text) => text.toLowerCase().includes(lowered));
}

function menuItems(
  phrases: readonly string[],
  matches: readonly string[],
  pick: PhraseMenuItem['pick'],
): PhraseMenuItem[] {
  return matches.slice(0, MAX_MENU).map((text) => ({
    key: `${pick}:${text}`,
    label: `${phrases.indexOf(text) + 1}. ${text}`,
    text,
    pick,
  }));
}

function projectSave(args: string, phrases: readonly string[]): PhraseView {
  const text = args.trim();
  if (text === '') return hold('用法：/save <內容>');
  if (text.startsWith('/')) return hold('片語不能用 / 開頭');
  if (phrases.includes(text)) return hold(`已經有「${text}」這句`);
  return {
    menu: [],
    hint: `Enter 存成片語「${text}」`,
    enter: { kind: 'save', phrases: addPhrase(phrases, text), notice: `已存成片語「${text}」` },
  };
}

function projectUnsave(args: string, phrases: readonly string[]): PhraseView {
  const index = oneBased(args, phrases.length);
  const exact = index === null ? (phrases.includes(args) ? args : undefined) : phrases[index];
  const matches = exact !== undefined ? [exact] : findPhrases(args, phrases);
  const menu = menuItems(phrases, matches, 'unsave');
  if (exact !== undefined) {
    return {
      menu,
      hint: `Enter 刪掉片語「${exact}」`,
      enter: {
        kind: 'unsave',
        phrases: removePhrase(phrases, exact),
        notice: `已刪掉片語「${exact}」`,
      },
    };
  }
  const hint = args === '' ? '用法：/unsave <序號|內容>' : `沒有「${args}」這句；用序號或完整內容`;
  return hold(hint, menu);
}

function projectInsert(query: string, phrases: readonly string[]): PhraseView {
  const matches = findPhrases(query, phrases);
  const menu = menuItems(phrases, matches, 'insert');
  const first = matches[0];
  if (first !== undefined) {
    return {
      menu,
      hint: `Enter 帶入「${first}」，再按一次 Enter 送出`,
      enter: { kind: 'insert', text: first },
    };
  }
  if (phrases.length === 0) return hold('還沒有片語；/save <內容> 新增一句');
  return hold(`沒有片語含「${query}」`);
}

export function waterfallPhraseView(draft: string, phrases: readonly string[]): PhraseView {
  const slash = slashParts(draft);
  if (!slash) return SEND;
  if (slash.name === 'save') return projectSave(slash.args, phrases);
  if (slash.name === 'unsave') return projectUnsave(slash.args, phrases);
  if (REFUSED.has(slash.name)) return hold('瀑布流只跟收件匣共用片語，沒有這個指令');
  return projectInsert(slash.query, phrases);
}
